import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const NAV_LINKS = [
  { label: '서비스', href: '#services' },
  { label: '포트폴리오', href: '#portfolio' },
  { label: '회사소개', href: '#about' },
  { label: '연락처', href: '#contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // 홈이 아닌 페이지에선 항상 흰 배경 + 어두운 텍스트
  const isScrolledStyle = !isHome || scrolled;

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    if (isHome) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isHome) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolledStyle ? 'bg-white shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <a
          href="/"
          onClick={handleLogoClick}
          className="flex items-center gap-3 cursor-pointer"
        >
          <img
            src="https://public.readdy.ai/ai/img_res/647a8474-0078-433a-9220-18bd877ee07e.png"
            alt="라인 인테리어 로고"
            className="h-14 w-auto object-contain"
          />
          <span
            className={`hidden sm:flex flex-col leading-none transition-colors duration-300 ${
              isScrolledStyle ? 'text-stone-800' : 'text-white'
            }`}
          >
            <span
              className="text-lg font-bold tracking-widest uppercase"
              style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: '0.15em' }}
            >
              Line
            </span>
            <span
              className="text-xs font-semibold tracking-[0.3em] uppercase opacity-80"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Interior
            </span>
          </span>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNavClick(link.href)}
              className={`text-sm font-medium tracking-wide transition-colors cursor-pointer whitespace-nowrap ${
                isScrolledStyle
                  ? 'text-stone-700 hover:text-stone-900'
                  : 'text-white/90 hover:text-white'
              }`}
            >
              {link.label}
            </button>
          ))}
          <button
            onClick={() => handleNavClick('#contact')}
            className="ml-2 px-5 py-2.5 rounded-md bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            무료 견적 신청
          </button>
        </nav>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="메뉴 열기"
        >
          <i
            className={`text-2xl ${isScrolledStyle ? 'text-stone-800' : 'text-white'} ${
              menuOpen ? 'ri-close-line' : 'ri-menu-line'
            }`}
          />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-stone-100 px-4 py-4 flex flex-col gap-2">
          {NAV_LINKS.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNavClick(link.href)}
              className="text-left py-3 px-2 text-stone-700 font-medium text-sm border-b border-stone-100 cursor-pointer whitespace-nowrap"
            >
              {link.label}
            </button>
          ))}
          <button
            onClick={() => handleNavClick('#contact')}
            className="mt-2 w-full py-3 rounded-md bg-blue-600 text-white text-sm font-semibold cursor-pointer whitespace-nowrap"
          >
            무료 견적 신청
          </button>
        </div>
      )}
    </header>
  );
}
